import React from "react";
import CountUp from "react-countup";
import { BsFillBagDashFill, BsLayersFill, BsArrowRepeat, BsClockFill, BsCheckCircleFill } from "react-icons/bs";
import { GiMoneyStack, GiNotebook } from "react-icons/gi";
import { RiMotorbikeFill } from "react-icons/ri";
import Select from "../components/Select";
import useMainDashboard from '../hooks/logics/dashboard/use-main-dashboard';

const Card = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | null;
}) => {
  return (
    value !== null && (
      <div className="px-6 shadow-lg flex w-full items-center rounded-lg h-32 bg-white">
        <div className="mr-4 flex justify-center">
          <div className="w-14 h-14 bg-blue-100 flex items-center justify-center rounded-full">
            {icon}
          </div>
        </div>
        <div>
          <p className="text-3xl font-semibold">
            <CountUp duration={0.5} end={value} />
          </p>
          <p className="text-gray-500 text-md">{label}</p>
        </div>
      </div>
    )
  );
};

const Dashboard = () => {

  const {
    allStats,
    extendedStats,
    filteredStats,
    isLoading,
    filteredStatsLoading,
    filter,
    handleFilter
  } = useMainDashboard();


  return (
    <>
      <div
        // style={{ minHeight: "calc(100vh - 100px)" }}
        className="flex flex-col justify-center py-10 md:py-5"
      >
        <div>
          {isLoading ? (
            <div className="w-full flex items-center justify-center">
              <div className="loader"></div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 w-full lg:grid-cols-3 place-items-center">
                <Card
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-blue-500"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                        clipRule="evenodd"
                      />
                    </svg>
                  }
                  value={allStats?.users ?? null}
                  label="Total users registered"
                />

                <Card
                  value={allStats?.drivers ?? null}
                  label="Total drivers registered"
                  icon={
                    <div className="w-14 h-14 bg-purple-100 flex items-center justify-center rounded-full">
                      <RiMotorbikeFill
                        className="text-purple-400"
                        fontSize="1.5rem"
                      />
                    </div>
                  }
                />

                <Card
                  icon={
                    <div className="w-14 h-14 bg-green-200 flex items-center justify-center rounded-full">
                      <BsFillBagDashFill
                        className="text-green-500"
                        fontSize="1.5rem"
                      />
                    </div>
                  }
                  label="Active orders"
                  value={extendedStats?.activeOrders ?? null}
                />
              </div>

              <div className="bg-gray-200 rounded-lg mt-10 pb-10 px-4 lg:px-10 py-10">
                <div className="w-full flex justify-end">
                  <div className="flex items-center">
                    <label htmlFor="filter">Filter by time:</label>
                    <Select
                      value={filter}
                      options={[
                        { label: "All", value: "all" },
                        { label: "Today", value: "1" },
                        { label: "Last week", value: "7" },
                        { label: "Last month", value: "30" },
                      ]}
                      onChange={(e) => handleFilter(e.target.value)}
                      id="filter"
                      className="w-32 h-8 bg-white ml-2"
                    />
                  </div>
                </div>

                {filteredStatsLoading ? (
                  <div className="w-full flex justify-center items-center mt-8">
                    <div className="loader"></div>
                  </div>
                ) : (
                  <>
                    <div className="text-black">
                      <p className="font-semibold text-lg">Order overview:</p>
                    </div>
                    <div className="grid grid-cols-1 gap-10 place-items-center lg:grid-cols-3 my-5 w-full justify-center">

                      <Card
                        icon={
                          <div className="w-14 h-14 bg-green-100 flex items-center justify-center rounded-full">
                            <GiNotebook className="text-green-400" fontSize="1.5rem" />
                          </div>
                        }
                        label="All orders"
                        value={filteredStats?.ordersCount ?? null}
                      />

                      <Card
                        icon={
                          <div className="w-14 h-14 bg-green-200 flex items-center justify-center rounded-full">
                            <GiMoneyStack className="text-green-500" fontSize="1.5rem" />
                          </div>
                        }
                        label="Completed orders"
                        value={filteredStats?.completedOrders ?? null}
                      />

                      <Card
                        icon={
                          <div className="w-14 h-14 bg-red-100 flex items-center justify-center rounded-full">
                            <BsFillBagDashFill className="text-red-500" fontSize="1.5rem" />
                          </div>
                        }
                        label="Cancelled orders"
                        value={filteredStats?.cancelledOrders ?? null}
                      />

                    </div>
                    <div className="text-black">
                      <p className="font-semibold text-lg">Batch overview:</p>
                    </div>
                    <div className="grid grid-cols-1 gap-5 place-items-center lg:grid-cols-4 my-5 w-full justify-center">

                      <Card
                        icon={
                          <div className="w-14 h-14 bg-indigo-100 flex items-center justify-center rounded-full">
                            <BsLayersFill className="text-indigo-500" fontSize="1.5rem" />
                          </div>
                        }
                        label="Total Batches"
                        value={filteredStats?.totalBatches ?? null}
                      />
                      <Card
                        icon={
                          <div className="w-14 h-14 bg-blue-100 flex items-center justify-center rounded-full">
                            <BsArrowRepeat className="text-blue-500" fontSize="1.5rem" />
                          </div>
                        }
                        label="Active Batches"
                        value={filteredStats?.activeBatches ?? null}
                      />  <Card
                        icon={
                          <div className="w-14 h-14 bg-amber-100 flex items-center justify-center rounded-full">
                            <BsClockFill className="text-amber-500" fontSize="1.5rem" />
                          </div>
                        }
                        label="Pending Batches"
                        value={filteredStats?.pendingBatches ?? null}
                      />  <Card
                        icon={
                          <div className="w-14 h-14 bg-green-100 flex items-center justify-center rounded-full">
                            <BsCheckCircleFill className="text-green-500" fontSize="1.5rem" />
                          </div>
                        }
                        label="Completed Batches"
                        value={filteredStats?.completedBatches ?? null}
                      />
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Dashboard;